import { useTranslation } from "react-i18next";
import { MobileCardRow, MobileCardWrapper, TableBody, TableColumn, TableHeader, TableRow, TableCell } from "./dashboard/table-components";
import { SearchIcon, EditIcon, DeleteIcon, ArrowLeftIcon, ArrowRightIcon, ReactivateIcon } from "../assets/dashboard-icon.svg";
import { useRef, useState } from "react";
import { Freelancer } from "../api/customer/index";
import { useFreelancers } from "../hooks/useFreelancers";
import { DashboardButton } from "./dashboard/dashboard-button.component";
import AddFreelancerModal from "../components/add-freelancer-modal/index";
import { Skeleton } from '@mui/material';
import { getStatusBadgeClass } from '../helper/common.helper';
import './../scss/components/invoice-table-detailed.style.scss';


export const MyFreelancerSection = ({
    className,
}: {
    className?: string
}
) => {
    const { t } = useTranslation();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isMobile = window.innerWidth <= 768;
    const [isFreelancerModal, setIsFreelancerModal] = useState(false)
    const {
        freelancers: paginatedFreelancers,
        loading,
        error,
        searchQuery,
        currentPage,
        totalPages,
        totalCount,
        pageSize,
        setSearchQuery,
        setCurrentPage,
        goToNextPage,
        goToPreviousPage,
        // Reactivate freelancer functionality
        isReactivate,
        setIsReactivate,
        handleReactivateFreelancer,
        reactivateLoading,
    } = useFreelancers(10);
    const PAGE_SIZE = 10;

    const getClientInitial = (name?: string) => {
        const parts = (name || '').trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '?';
        return parts.slice(0, 2).map((p) => p.charAt(0)).join('').toUpperCase();
    };

    const getAvatarVariant = (name?: string) => {
        const key = (name || '').trim();
        let sum = 0;
        for (let i = 0; i < key.length; i++) sum += key.charCodeAt(i);
        return (sum % 5) + 1;
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const onReactivateFreelancer = (id: number) => {
        handleReactivateFreelancer(id, t('FREELANCER_REACTIVATED_SUCCESS'));
    };

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (
                let i = Math.max(2, currentPage - 1);
                i <= Math.min(totalPages - 1, currentPage + 1);
                i++
            ) {
                if (!pages.includes(i)) pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };
    return (
        <div className={`invoice-table-container my-3 ${className}`}>
            <div className="invoice-table-header">
                <h3 className="invoice-table-title">
                    {t('MY_FREELANCERS')}
                    <span className="mi-count-badge">{totalCount ?? paginatedFreelancers.length}</span>
                </h3>

                <div className="invoice-table__filter-bar">
                    <div className="invoice-table__search-input" ref={dropdownRef}>
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder={t('SEARCH_FREELANCER', { defaultValue: 'Search freelancer' })}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="invoice-table__search-field"
                        />

                    </div>
                    <DashboardButton
                        variant="primary"
                        // icon={<PlusIcon />}
                        onClick={() => setIsFreelancerModal(true)}
                    >
                        {t('ADD_FREELANCER')}
                    </DashboardButton>
                </div>
            </div>

            <div className="invoice-table-content">

                {/* Loading shimmer — replaces only the table BODY
                    while fetching. The real .invoice-table-header
                    (title + count) and .invoice-table__filter-bar
                    (search input + Add Freelancer button) stay
                    mounted across re-fetches. Previously the WHOLE
                    component early-returned behind <InvoiceTableSkeleton>
                    on every keystroke in the search box, which read
                    as a full-page refresh and stole focus from the
                    input mid-type. Reusing the table's real BEM
                    classes (.invoice-table__body / __desktop-row /
                    __desktop-cell) so the shimmer occupies the same
                    column widths as the real rows. */}
                {loading && (
                    <div className="freelancer-table-scroll-wrapper">
                        <div className="invoice-table">
                            <div className="invoice-table__body">
                                {Array.from({ length: 6 }).map((_, r) => (
                                    <div className="invoice-table__desktop-row" key={r}>
                                        {Array.from({ length: 7 }).map((_, c) => (
                                            <div className="invoice-table__desktop-cell" key={c}>
                                                <Skeleton variant="text" width="72%" height={16} animation="wave" />
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Table */}
                {!loading && !error && paginatedFreelancers.length >= 0 && (
                    <div className="freelancer-table-scroll-wrapper">
                        <div className="invoice-table">
                            {!isMobile && (
                                <TableHeader>
                                    <TableColumn>{t('SERIAL_ID')}</TableColumn>
                                    <TableColumn>{t('USER_ID')}</TableColumn>
                                    <TableColumn>{t('FREELANCER_NAME')}</TableColumn>
                                    <TableColumn>{t('EMAIL')}</TableColumn>
                                    <TableColumn>{t('PHONE_NO')}</TableColumn>
                                    <TableColumn>{t('STATUS')}</TableColumn>
                                    <TableColumn>{t('ACTIONS', { defaultValue: 'Actions' })}</TableColumn>
                                </TableHeader>
                            )}

                            <TableBody>
                                {/* Loading State */}
                                {loading && (
                                    <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                                        {t('LOADING_FREELANCERS') || 'Loading freelancers...'}
                                    </div>
                                )}
                                {isMobile ? (
                                    // Mobile: Card-based layout
                                    paginatedFreelancers?.map((freelancer: Freelancer, index) => (
                                        <MobileCardWrapper key={freelancer.id}>
                                            <MobileCardRow label={t('SERIAL_ID')}>
                                                <span className="mi-invoice__number">
                                                    {(currentPage - 1) * PAGE_SIZE + index + 1}
                                                </span>
                                            </MobileCardRow>
                                            <MobileCardRow label={t('USER_ID')}>
                                                <span className="mi-invoice__number">{freelancer?.signUpUser.userId || 'N/A'}</span>
                                            </MobileCardRow>
                                            <MobileCardRow label={t('FREELANCER_NAME')}>
                                                <span className="mi-client">
                                                    <span className={`mi-client__avatar mi-client__avatar--v${getAvatarVariant(freelancer.signUpUser.firstName)}`}>{getClientInitial(freelancer.signUpUser.firstName)}</span>
                                                    <span className="mi-client__name">{freelancer.signUpUser.firstName || 'N/A'}</span>
                                                </span>
                                            </MobileCardRow>
                                            <MobileCardRow label={t('EMAIL')}>
                                                {freelancer.signUpUser.email || 'N/A'}
                                            </MobileCardRow>
                                            <MobileCardRow label={t('PHONE_NO')}>
                                                {freelancer.signUpUser.phoneNo || 'N/A'}
                                            </MobileCardRow>
                                            <MobileCardRow label={t('STATUS')}>
                                                <span className={`${getStatusBadgeClass(freelancer.signUpUser.isActive ? 2 : 1)} mi-status-badge`}>
                                                    <span className="mi-dot" />
                                                    {freelancer.signUpUser.isActive ? t('ACTIVE') : t('INACTIVE')}
                                                </span>
                                            </MobileCardRow>
                                            <MobileCardRow label={t('REACTIVATE_FREELANCER')}>
                                                <div className="invoice-table-detailed__action-group">
                                                    <button
                                                        className="invoice-table-detailed__action-btn"
                                                        data-tip={t("REACTIVATE")}
                                                        disabled={freelancer.signUpUser.isActive}
                                                        onClick={() => {
                                                            setIsReactivate({
                                                                show: true,
                                                                id: Number(freelancer.signUpUser.userId) || 0,
                                                                name: freelancer.signUpUser.firstName || '',
                                                            });
                                                            onReactivateFreelancer(Number(freelancer.signUpUser.userId) || 0);
                                                        }}

                                                    >
                                                        <ReactivateIcon opacity={freelancer.signUpUser.isActive ? 0.5 : 1} />
                                                    </button>

                                                </div>
                                            </MobileCardRow>
                                        </MobileCardWrapper>
                                    ))
                                ) : (
                                    paginatedFreelancers.map((freelancer: Freelancer, index) => (
                                        <TableRow key={freelancer.id}>
                                            <TableCell>
                                                <span className="mi-invoice__number">{(currentPage - 1) * PAGE_SIZE + index + 1}</span>
                                            </TableCell>
                                            <TableCell><span className="mi-invoice__number">{freelancer.signUpUser.userId || 'N/A'}</span></TableCell>
                                            <TableCell>
                                                <span className="mi-client">
                                                    <span className={`mi-client__avatar mi-client__avatar--v${getAvatarVariant(freelancer.signUpUser.firstName)}`}>{getClientInitial(freelancer.signUpUser.firstName)}</span>
                                                    <span className="mi-client__name" title={freelancer.signUpUser.firstName || ''}>{freelancer.signUpUser.firstName || 'N/A'}</span>
                                                </span>
                                            </TableCell>
                                            <TableCell><span title={freelancer.signUpUser.email || ''}>{freelancer.signUpUser.email || 'N/A'}</span></TableCell>
                                            <TableCell>{freelancer.signUpUser.phoneNo || 'N/A'}</TableCell>
                                            <TableCell>
                                                <span className={`${getStatusBadgeClass(freelancer.signUpUser.isActive ? 2 : 1)} mi-status-badge`}>
                                                    <span className="mi-dot" />
                                                    {freelancer.signUpUser.isActive ? t('ACTIVE') : t('INACTIVE')}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="invoice-table-detailed__action-group">
                                                    <button
                                                        className="invoice-table-detailed__action-btn"
                                                        data-tip={t("REACTIVATE_FREELANCER")}
                                                        disabled={freelancer.signUpUser.isActive}
                                                        onClick={() => {
                                                            setIsReactivate({
                                                                show: true,
                                                                id: Number(freelancer.signUpUser.userId) || 0,
                                                                name: freelancer.signUpUser.firstName || '',
                                                            });
                                                            onReactivateFreelancer(Number(freelancer.signUpUser.userId) || 0);
                                                        }}
                                                    >
                                                        <ReactivateIcon opacity={freelancer.signUpUser.isActive ? 0.5 : 1} />
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </div>

                        {/* Themed empty state — violet illustration + title +
                            contextual subtitle. Lifted OUT of <TableBody>
                            because that element is `display: table-row-group`,
                            which boxes any non-row child into an anonymous
                            cell in the first column (~140px wide, left-pinned).
                            Sitting it here as a sibling of .invoice-table puts
                            it in normal block flow so it spans the full scroll
                            wrapper width and centers cleanly under the violet
                            header bar.

                            All inline-styled so the .sn-empty SCSS (which is
                            nested under .salary-notice-page in
                            salary-notice-page.style.scss) doesn't need to be
                            in this page's ancestor chain. */}
                        {paginatedFreelancers.length === 0 && !loading && (
                            <div
                                role="status"
                                aria-live="polite"
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '64px 24px',
                                    textAlign: 'center',
                                    gap: 14,
                                    /* Gentle entrance: fade + lift from 8px below
                                       so the empty state doesn't pop in jarringly
                                       when search results clear. */
                                    animation: 'sn-empty-enter 0.45s ease-out both',
                                }}
                            >
                                <div
                                    aria-hidden="true"
                                    style={{
                                        position: 'relative',
                                        width: 132,
                                        height: 132,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {/* Soft pulsing halo behind the illustration. */}
                                    <span
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            borderRadius: '50%',
                                            background: 'radial-gradient(closest-side, rgba(168, 85, 247, 0.22), rgba(168, 85, 247, 0))',
                                            animation: 'sn-empty-halo 3.2s ease-in-out infinite',
                                        }}
                                    />
                                    <svg width="96" height="96" viewBox="0 0 64 64" fill="none" style={{ position: 'relative', zIndex: 1, animation: 'sn-empty-float 4s ease-in-out infinite' }}>
                                        <defs>
                                            <linearGradient id="freelancer-empty-grad" x1="10" y1="12" x2="54" y2="22" gradientUnits="userSpaceOnUse">
                                                <stop stopColor="#a855f7" />
                                                <stop offset="1" stopColor="#7c3aed" />
                                            </linearGradient>
                                        </defs>
                                        <rect x="10" y="12" width="44" height="40" rx="6" fill="#fff" stroke="#7c3aed" strokeOpacity="0.25" strokeWidth="2" />
                                        <path d="M10 18a6 6 0 0 1 6-6h32a6 6 0 0 1 6 6v3H10z" fill="url(#freelancer-empty-grad)" />
                                        <line x1="18" y1="30" x2="46" y2="30" stroke="#7c3aed" strokeOpacity="0.18" strokeWidth="3" strokeLinecap="round" strokeDasharray="2 5" />
                                        <line x1="18" y1="38" x2="40" y2="38" stroke="#7c3aed" strokeOpacity="0.18" strokeWidth="3" strokeLinecap="round" strokeDasharray="2 5" />
                                        <line x1="18" y1="46" x2="44" y2="46" stroke="#7c3aed" strokeOpacity="0.12" strokeWidth="3" strokeLinecap="round" strokeDasharray="2 5" />
                                        <circle cx="44" cy="42" r="9" fill="#fff" stroke="#7c3aed" strokeWidth="2.5" />
                                        <line x1="50.5" y1="48.5" x2="56" y2="54" stroke="#7c3aed" strokeWidth="3" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <p style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 16, color: '#1e1b4b' }}>
                                    {t('NO_FREELANECER_DATA_FOUND', { defaultValue: 'No freelancers found' })}
                                </p>
                                <p style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500, fontSize: 13.5, color: '#5b21b6', opacity: 0.78, maxWidth: 380 }}>
                                    {searchQuery
                                        ? t('NO_RESULTS_TRY_DIFFERENT_SEARCH', { defaultValue: 'No matches for your search. Try a different name or email.' })
                                        : t('ADD_FREELANCER_TO_GET_STARTED', { defaultValue: 'Add your first freelancer to get started.' })}
                                </p>
                                <style>{`
                                    @keyframes sn-empty-halo {
                                        0%, 100% { transform: scale(1); opacity: 0.55; }
                                        50%      { transform: scale(1.08); opacity: 0.85; }
                                    }
                                    @keyframes sn-empty-enter {
                                        from { opacity: 0; transform: translateY(8px); }
                                        to   { opacity: 1; transform: translateY(0); }
                                    }
                                    @keyframes sn-empty-float {
                                        0%, 100% { transform: translateY(0); }
                                        50%      { transform: translateY(-4px); }
                                    }
                                `}</style>
                            </div>
                        )}
                    </div>
                )}

                {/* Footer: count + pagination */}
                {paginatedFreelancers.length > 0 && (
                    <div className="invoice-table-footer">
                        <span className="invoice-table-footer__count">
                            {(() => {
                                const total = totalCount ?? 0;
                                const size = pageSize ?? PAGE_SIZE;
                                const start = total === 0 ? 0 : (currentPage - 1) * size + 1;
                                const end = Math.min(currentPage * size, total);
                                return t('SHOWING_FREELANCERS_COUNT', {
                                    start,
                                    end,
                                    total,
                                    defaultValue: 'Showing {{start}}–{{end}} of {{total}} freelancers',
                                });
                            })()}
                        </span>
                        {totalPages > 1 && (
                        <div className="invoice-table-pagination">
                        <div className="pagination-controls">
                            <button
                                className="pagination-button pagination-button--prev"
                                onClick={goToPreviousPage}
                                disabled={currentPage === 1}
                            >
                                <ArrowLeftIcon />
                                <span>{t('PREV_STEP') || 'Prev'}</span>
                            </button>

                            {getPageNumbers().map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <button
                                            key={`ellipsis-${index}`}
                                            className="pagination-button pagination-button--number"
                                            disabled
                                        >
                                            ...
                                        </button>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        className={`pagination-button pagination-button--number${page === currentPage ? ' pagination-button--active' : ''
                                            }`}
                                        onClick={() => setCurrentPage(page as number)}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            <button
                                className="pagination-button pagination-button--next"
                                onClick={goToNextPage}
                                disabled={currentPage === totalPages}
                            >
                                <span>{t('NEXT_STEP') || 'Next'}</span>
                                <ArrowRightIcon />
                            </button>
                        </div>
                        </div>
                        )}
                    </div>
                )}
                 <AddFreelancerModal show={isFreelancerModal} onClose={() => setIsFreelancerModal(false)}/>
            </div>
        </div>
    );
}